import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  BookOpen,
  Mail,
  ExternalLink,
  PhoneCall,
  ArrowUpRight,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import CallForwardingGuide from '@/components/page-components/help/CallForwardingGuide';
import CopyablePhoneNumber from '@/components/common/CopyablePhoneNumber';
import { selectAgents } from '@/redux/agentsSlice';
import { getDocsUrl, SUPPORT_EMAIL } from '@/utils/support';

/**
 * The in-app help page.
 *
 * Deliberately narrow. Explanations live in the documentation site, which is
 * public and can be linked to from anywhere; what stays here is the handful of
 * things that need the signed-in account — above all the forwarding guide,
 * which fills in this user's own Dialup number and copies the operator codes.
 */
const Help: React.FC = () => {
  const { t } = useTranslation();
  const agents = useSelector(selectAgents);

  const agentsWithNumbers = agents.filter(
    agent => agent.phone_number || agent.subscription?.sip_number
  );

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="space-y-6 sm:space-y-8">
        <section className="flex flex-col space-y-6 sm:space-y-8 w-full">
          <div className="flex flex-col space-y-2">
            <Label className="text-2xl sm:text-3xl font-semibold">
              {t('sidebar.help.translation')}
            </Label>
            <Label className="text-sm text-muted-foreground">
              {t(
                'help.page.subtitle.translation',
                'Set up call forwarding, find your number, and get in touch when you need us.'
              )}
            </Label>
          </div>

          {/* Documentation first: most questions are answered there, and this
              page should hand them over rather than restate them. */}
          <Card className="relative w-full overflow-hidden bg-gradient-to-r from-gradient-start/10 to-gradient-end/10">
            <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-gradient-start to-gradient-end" />
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {t(
                        'help.page.docs.title.translation',
                        'Dialup documentation'
                      )}
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {t(
                        'help.page.docs.description.translation',
                        'Setup guides, how the agent works, and answers to common problems.'
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  asChild
                  variant="gradient"
                  className="shrink-0 text-primary-foreground"
                >
                  <a href={getDocsUrl()} target="_blank" rel="noreferrer">
                    {t('help.page.docs.action.translation', 'Open docs')}
                    <ExternalLink className="ml-1.5 h-4 w-4" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Your numbers — the one piece of forwarding setup that cannot be
              looked up in public documentation. */}
          {agentsWithNumbers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneCall className="h-5 w-5" />
                  {t('help.page.numbers.title.translation', 'Your numbers')}
                </CardTitle>
                <CardDescription>
                  {t(
                    'help.page.numbers.description.translation',
                    'The number callers reach each agent on.'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {agentsWithNumbers.map(agent => (
                    <li
                      key={agent.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2.5 first:pt-0 last:pb-0"
                    >
                      <span className="min-w-0 truncate text-sm font-medium text-foreground">
                        {agent.name}
                      </span>
                      <CopyablePhoneNumber
                        number={
                          agent.phone_number ||
                          agent.subscription?.sip_number ||
                          ''
                        }
                      />
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <CallForwardingGuide />

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {t('help.page.support.title.translation', 'Contact support')}
              </CardTitle>
              <CardDescription>
                {t(
                  'help.page.support.description.translation',
                  'Tell us which agent, when the call happened, and what you expected.'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-3">
              <Button asChild variant="outline">
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </Button>
              <Button asChild variant="ghost">
                <a
                  href={getDocsUrl('/help/getting-in-touch')}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('help.page.support.guide.translation', 'What to include')}
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Help;
